/**
 * User file cache utilities
 * Uses session storage with LRU eviction policy
 * Cache is keyed by retrieveId (the userId whose files are being fetched)
 */

// Maximum number of users to cache (TODO: Move to user settings - see issue #113)
export const MAX_CACHED_USERS = 10;

const CACHE_PREFIX = 'user_files_';
const LRU_KEY = 'user_files_lru';

// Schema version — bump when the cached file-list shape changes so stale
// sessions don't serve incompatible data. v2: era-dated protocol/compliance
// filenames (retrieve returns one protocol + one compliance file). v3: every
// file carries S3 `lastModified`; without it reconcileMerges reads each one as
// undateable, holds every merged document's claim and stops hiding superseded
// sources, so allTests counts the same marker twice for the rest of the session.
const CACHE_VERSION = 3;
const CACHE_VERSION_KEY = 'user_files_cache_version';

(function ensureCacheVersion() {
    try {
        const current = sessionStorage.getItem(CACHE_VERSION_KEY);
        if (current !== String(CACHE_VERSION)) {
            const lru = JSON.parse(sessionStorage.getItem(LRU_KEY) || '[]');
            lru.forEach(id => sessionStorage.removeItem(`${CACHE_PREFIX}${id}`));
            sessionStorage.removeItem(LRU_KEY);
            sessionStorage.setItem(CACHE_VERSION_KEY, String(CACHE_VERSION));
        }
    } catch { /* sessionStorage unavailable (SSR/tests) — cache is best-effort */ }
})();

/**
 * Get cached files for a user
 * @param {string} retrieveId - User UUID whose files to retrieve
 * @returns {Array|null} Cached files or null if not found
 */
export function getCachedFiles(retrieveId) {
    try {
        const cacheKey = `${CACHE_PREFIX}${retrieveId}`;
        const cached = sessionStorage.getItem(cacheKey);

        if (cached) {
            const parsed = JSON.parse(cached);
            updateLRU(retrieveId);
            return parsed;
        }
        return null;
    } catch (error) {
        console.error('Error reading file cache:', error);
        return null;
    }
}

/**
 * Cache files for a user
 * @param {string} retrieveId - User UUID whose files to cache
 * @param {Array} files - Files to cache
 */
export function cacheFiles(retrieveId, files) {
    try {
        if (!files || !Array.isArray(files) || files.length === 0) return;
        const cacheKey = `${CACHE_PREFIX}${retrieveId}`;
        sessionStorage.setItem(cacheKey, JSON.stringify(files));
        updateLRU(retrieveId);
    } catch (error) {
        console.error('Error caching files:', error);
    }
}

/**
 * Update LRU list and evict oldest if necessary
 * @param {string} retrieveId - User UUID to mark as recently used
 */
function updateLRU(retrieveId) {
    try {
        let lru = JSON.parse(sessionStorage.getItem(LRU_KEY) || '[]');

        // Remove if exists (we'll add to end)
        lru = lru.filter(id => id !== retrieveId);

        // Add to end (most recently used)
        lru.push(retrieveId);

        // Evict oldest if over limit
        if (lru.length > MAX_CACHED_USERS) {
            const evicted = lru.shift();
            const evictedKey = `${CACHE_PREFIX}${evicted}`;
            sessionStorage.removeItem(evictedKey);
            console.log(`Evicted user ${evicted} from cache (LRU)`);
        }

        sessionStorage.setItem(LRU_KEY, JSON.stringify(lru));
    } catch (error) {
        console.error('Error updating LRU:', error);
    }
}

/**
 * Clear cache for a specific user or all users
 * @param {string|null} retrieveId - User UUID to clear, or null for all
 */
export function clearFileCache(retrieveId = null) {
    try {
        if (retrieveId) {
            // Clear specific user
            const cacheKey = `${CACHE_PREFIX}${retrieveId}`;
            sessionStorage.removeItem(cacheKey);

            // Remove from LRU
            const lru = JSON.parse(sessionStorage.getItem(LRU_KEY) || '[]');
            const updated = lru.filter(id => id !== retrieveId);
            sessionStorage.setItem(LRU_KEY, JSON.stringify(updated));

            console.log(`Cleared cache for user ${retrieveId}`);
        } else {
            // Clear all user file caches
            const lru = JSON.parse(sessionStorage.getItem(LRU_KEY) || '[]');
            lru.forEach(id => {
                sessionStorage.removeItem(`${CACHE_PREFIX}${id}`);
            });
            sessionStorage.removeItem(LRU_KEY);
            console.log('Cleared all user file caches');
        }
    } catch (error) {
        console.error('Error clearing file cache:', error);
    }
}
